import { Col, Form, Row } from "react-bootstrap";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  BackButton,
  Box,
  ButtonStyled,
  SpanStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import { LazyLoadDropdown } from "../../../components/common/inputFields/LazyLoadDropdown";
import {
  LeftArrowBlack,
  RightArrowWhite,
  RightArrowWithoutBar,
} from "../../../components/styled/AllImages";
import DynamicTableList from "../../../components/common/tables/DynamicTableList";
import SelectProductModal from "./components/modals/SelectProductModal";
import { useEffect, useState } from "react";
import SelectCategoryModal from "./components/modals/SelectCategoryModal";
import { CreateQuotationListColumns } from "../../../helpers/adminConstants/TableConstants";
import {
  formatAmountForDisplay,
  formatDate,
  formatToUTF,
} from "../../../utils";
import { toast } from "react-toastify";
import ClientsApi from "../../../services/api/clientsApi";
import { useUser } from "../../../context/userContext";
import { useSelector } from "react-redux";
import DatePicker from "../../../components/common/inputFields/DatePicker";
import SalesInvoiceApi from "../../../services/api/salesInvoiceApi";

const discountOptions = [
  { value: 1, label: "Fixed Amount" },
  { value: 2, label: "Percentage" },
  { value: 3, label: "Buy One Get One" },
  { value: 4, label: "Seasonal Offer" },
  { value: 5, label: "Loyalty Discount" },
];

const columns = [
  { name: "category", label: "Category" },
  { name: "product", label: "Product" },
  { name: "quantity", label: "Quantity" },
];

const EditSalesInvoice = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      name: "",
      client_id: null,
      discount_type: null,
      discount: "",
      taxable_amount: "",
      tax: "",
      amount: "",
      notes: "",
      products: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "products",
    defaultValues: { products: [] },
  });

  const { productsList, categoriesList } = useSelector((state) => state.helper);

  const { user } = useUser();
  const vatValue = user?.organization?.vat_percentage;
  const vatRate = vatValue ? parseInt(vatValue) : 0;
  const watchedProducts = watch("products");

  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSelectProductModal, setShowSelectProductModal] = useState(false);
  const [showSelectCategoryModal, setShowSelectCategoryModal] = useState(false);
  const [inputRow, setInputRow] = useState({
    category: "",
    product: "",
    quantity: "",
    unit_price: "",
    subtotal: "",
  });
  const [clientOptions, setClientOptions] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const currentRow = getValues("current_input_row");

  const handleAddItemRow = () => {
    const newRow = getValues("current_input_row");

    if (!newRow?.product || !newRow?.quantity || !newRow?.unit_price) return;
    append({
      ...newRow,
    });
    setValue("current_input_row", {});
  };
  const calculateSummary = () => {
    let grossTotal = 0; // total before discount
    let totalDiscount = 0; // all discounts
    let netTotal = 0; // total after discounts
    let totalVat = 0; // sum of VAT
    let grandTotal = 0; // final payable amount

    watchedProducts?.forEach((p) => {
      const total = parseFloat(p.total) || 0;
      const discount = parseFloat(p.discount) || 0;
      const net = parseFloat(p.net_total) || 0;
      const vat = parseFloat(p.vat) || 0;
      const subtotal = parseFloat(p.subtotal) || 0;

      grossTotal += total;
      totalDiscount += discount;
      netTotal += net;
      totalVat += vat;
      grandTotal += subtotal;
    });
    setValue(`sub_total`, grossTotal.toFixed(2));
    setValue(`discount`, totalDiscount.toFixed(2));
    setValue(`amount_after_discount`, netTotal.toFixed(2));
    setValue(`tax_value`, totalVat.toFixed(2));
    setValue(`total_amount`, grandTotal.toFixed(2));

    return { grossTotal, totalDiscount, netTotal, totalVat, grandTotal };
  };

  const summary = calculateSummary();

  const recalculateRow = (index, rowData) => {
    const qty = parseFloat(rowData.quantity) || 0;
    const unitPrice = parseFloat(rowData.unit_price) || 0;
    const discount = parseFloat(rowData.discount) || 0;

    let total = qty * unitPrice;
    if (discount > total) {
      // prevent discount exceeding total
      toast.error(t("discount_cant_be_greater_then_total"));
      rowData.discount = 0;
    }

    let netTotal = total - (parseFloat(rowData.discount) || 0);
    let vat = (netTotal * vatRate) / 100;
    let subtotal = netTotal + vat;

    const updatedRow = {
      ...rowData,
      discount: rowData?.discount || "",
      total: total.toFixed(2),
      net_total: netTotal.toFixed(2),
      vat: vat.toFixed(2),
      subtotal: subtotal.toFixed(2),
    };

    if (index === "current_input_row") {
      setValue("current_input_row", { ...updatedRow });
    } else {
      // update(index, updatedRow);
      setValue(`products.${index}`, updatedRow);
    }
  };

  const getClientsList = async () => {
    try {
      const { data, code } = await ClientsApi.clientsList(1, "", null, false);
      if (code === 200 && data) {
        setClientOptions(
          data?.map((item, _) => ({
            ...item,
            value: item?.id,
            label: language === "en" ? item?.name_en : item?.name_ar,
          }))
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    getClientsList();
  }, [language]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const { data, code } = await SalesInvoiceApi.viewInvoice(id);
      if (code === 200 && data) {
        reset({
          name: data?.name,
          client_id: parseInt(data?.client_id),
          discount_type: data?.discount_type,
          discount: data?.discount,
          tax_value: data?.tax_value,
          sub_total: data?.sub_total,
          notes: data?.notes,
          amount_after_discount: data?.amount_after_discount,
        });
        const productsData =
          data?.products?.map((item) => ({
            ...item,
            product_id: item?.product_id,
            category_id: item?.category_id,
            product:
              language === "en" ? item?.product_name_en : item?.product_name_ar,
            category:
              language === "en"
                ? item?.category_name_en
                : item?.category_name_ar,
          })) || [];
        setDate(formatToUTF(data?.invoice_due));
        append(productsData);
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [language]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const dataObj = {
        ...data,
        invoice_id: id,
        discount_type: 1,
        invoice_due: date ? formatDate(date) : "",
        action: "update",
      };
      const { code, message } = await SalesInvoiceApi.createSalesInvoice(
        dataObj
      );
      if (code === 200) {
        toast.success(message);
        navigate(`/sales-invoices/${id}/view-invoice`);
      }
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("edit_invoice")}
            onClick={() => navigate(`/sales-invoices/${id}/view-invoice`)}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <Box className="content-container">
        {loading ? (
          <ThemeLoader />
        ) : (
          <FormWrapper>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <ValidatedInput
                    weight="600"
                    column={true}
                    start={true}
                    name="name"
                    type="input"
                    fieldType="text"
                    placeholder="invoice_for_office_supplies"
                    label="invoice_name"
                    className="combined px-0"
                    // staric="staric"
                    register={register}
                    errors={errors}
                    validationRules={{
                      required: true,
                    }}
                  />
                </Col>
                <Col md={6}>
                  <LazyLoadDropdown
                    label="to"
                    weight="600"
                    name="client_id"
                    control={control}
                    placeholder={t("select_client")}
                    options={clientOptions}
                    value={watch("client_id")}
                    register={register}
                    errors={errors}
                    validationRules={{ required: true }}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <DatePicker
                  weight="600"
                  value={date}
                  onChange={setDate}
                  label="invoice_due"
                  placeholder={t("select_invoice_due")}
                />
              </Row>
              <Row className="mt-3 mb-3">
                <DynamicTableList
                  watch={watch}
                  remove={remove}
                  setValue={setValue}
                  getValues={getValues}
                  cols={CreateQuotationListColumns}
                  register={register}
                  fields={fields}
                  inputRow={inputRow}
                  setInputRow={setInputRow}
                  handleAddItemRow={handleAddItemRow}
                  setShowSelectProductModal={setShowSelectProductModal}
                  selectedProduct={selectedProduct}
                  setShowSelectCategoryModal={setShowSelectCategoryModal}
                  selectedCategory={selectedCategory}
                  setEditingRowIndex={setEditingRowIndex}
                  recalculateRow={recalculateRow}
                  currentRow={currentRow}
                  setCurrentProduct={setCurrentProduct}
                />
              </Row>

              <Box className="mb-4">
                <ValidatedInput
                  weight="600"
                  name="notes"
                  column={true}
                  start={true}
                  type="textarea"
                  fieldType="text"
                  placeholder="write_notes"
                  label="notes"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Box>
              <Summary>
                <Box>
                  <p>
                    {t("subtotal")}:{" "}
                    <span>
                      {user?.organization?.currency?.stringCode || t("sar")}{" "}
                      {summary?.grossTotal.toFixed(2)}
                    </span>
                  </p>
                  <p>
                    {t("discount")}:{" "}
                    <span>
                      {user?.organization?.currency?.stringCode || t("sar")}{" "}
                      {summary?.totalDiscount.toFixed(2)}
                    </span>
                  </p>
                  <p>
                    {t("total_after_discount")}:{" "}
                    <span>
                      {user?.organization?.currency?.stringCode || t("sar")}{" "}
                      {summary?.netTotal.toFixed(2)}
                    </span>
                  </p>
                  <p>
                    {t("tax")}:{" "}
                    <span>
                      {user?.organization?.currency?.stringCode || t("sar")}{" "}
                      {summary?.totalVat.toFixed(2)}
                    </span>
                  </p>
                  <p>
                    {t("total_amount")}:{" "}
                    <span>
                      {user?.organization?.currency?.stringCode || t("sar")}{" "}
                      {summary?.grandTotal?.toFixed(2)}
                    </span>
                  </p>
                </Box>
              </Summary>

              <FooterButtons>
                <ButtonStyled
                  border="var(--borderExtraLight)"
                  iconBefore={LeftArrowBlack}
                  bg="transparent"
                  label={t("Cancel")}
                  color="var(--text)"
                  type="button"
                />
                <ButtonStyled
                  icon={RightArrowWhite}
                  type="submit"
                  label={t("update")}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  bg="var(--themeColor)"
                />
              </FooterButtons>
            </Form>
            <SelectProductModal
              optionsList={
                editingRowIndex === null
                  ? productsList.filter(
                      (p) =>
                        !watch("current_input_row.category_id") ||
                        Number(p.product_category_id) ===
                          Number(watch("current_input_row.category_id"))
                    )
                  : productsList.filter(
                      (p) =>
                        !watch(`products.${editingRowIndex}.category_id`) ||
                        Number(p.product_category_id) ===
                          Number(
                            watch(`products.${editingRowIndex}.category_id`)
                          )
                    )
              }
              selectedItem={currentProduct}
              title={t("select_product")}
              show={showSelectProductModal}
              onHide={() => setShowSelectProductModal(false)}
              onSelect={(selectedProduct) => {
                const unitPrice = parseFloat(selectedProduct?.price);
                setCurrentProduct(null);

                if (editingRowIndex === null) {
                  const input = watch("current_input_row") || {};
                  const dataObj = {
                    ...input,
                    product: selectedProduct.name,
                    product_id: selectedProduct?.id,
                    unit_price: unitPrice,
                    unit: selectedProduct?.unit_of_measurement,
                    category_id: selectedProduct?.product_category_id,
                    subtotal:
                      input.quantity && unitPrice
                        ? parseFloat(input.quantity) * unitPrice
                        : "",
                  };
                  recalculateRow("current_input_row", dataObj);
                } else {
                  const quantity = parseFloat(
                    watch(`products.${editingRowIndex}.quantity`) || 0
                  );
                  const rowData = {
                    ...watch(`products.${editingRowIndex}`),
                    product: selectedProduct?.name,
                    product_id: selectedProduct?.id,
                    category_id: selectedProduct?.product_category_id,
                    unit_price: unitPrice,
                    unit: selectedProduct?.unit_of_measurement,
                    subtotal: (unitPrice * quantity).toFixed(2),
                  };
                  recalculateRow(editingRowIndex, rowData);
                }

                setShowSelectProductModal(false);
                setEditingRowIndex(null);
              }}
            />
            <SelectCategoryModal
              optionsList={categoriesList}
              title={t("select_category")}
              show={showSelectCategoryModal}
              onHide={() => setShowSelectCategoryModal(false)}
              onSelect={(selectedCategory) => {
                if (editingRowIndex === null) {
                  console.log(editingRowIndex, "here in category");

                  const input = watch("current_input_row") || {};
                  setValue("current_input_row", {
                    ...input,
                    category_id: selectedCategory?.id,
                    category: selectedCategory?.name,
                  });
                } else {
                  setValue(
                    `products.${editingRowIndex}.category_id`,
                    selectedCategory?.id
                  );
                  setValue(
                    `products.${editingRowIndex}.category`,
                    selectedCategory?.name
                  );
                }

                setShowSelectCategoryModal(false);
                setEditingRowIndex(null);
              }}
            />
          </FormWrapper>
        )}
      </Box>
    </>
  );
};

export default EditSalesInvoice;

const FormWrapper = styled.div`
  padding: 30px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const Summary = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: end;
  font-size: 16px;

  p {
    margin: 0.25rem 0;
    color: var(--text);
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  span {
    color: var(--text);
    font-weight: 500;
  }
`;
